import { useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
  SELECTION_CHANGE_COMMAND,
  RangeSelection,
  $getRoot,
} from 'lexical';
import { $wrapSelectionInMarkNode, MarkNode } from '@lexical/mark';
import { CommentNode, $createCommentNode, $isCommentNode, type CommentData } from '../nodes/CommentNode';
import { CommentPopover } from '../components/CommentPopover';

export const ADD_COMMENT_COMMAND: LexicalCommand<CommentData> = createCommand('ADD_COMMENT_COMMAND');
export const TOGGLE_COMMENT_COMMAND: LexicalCommand<string> = createCommand('TOGGLE_COMMENT_COMMAND');
export const DELETE_COMMENT_COMMAND: LexicalCommand<string> = createCommand('DELETE_COMMENT_COMMAND');

/**
 * Wraps selection in comment nodes, handling splits by assigning the same groupId.
 * All split nodes from a single comment action will share the same groupId.
 */
function $wrapInComment(selection: RangeSelection, commentData: CommentData): void {
  // Ensure the comment has a groupId (use the comment id as default)
  if (!commentData.groupId) {
    commentData.groupId = commentData.id;
  }

  // Store nodes before wrapping to track what gets created
  const nodesBefore = new Set<string>();
  $getRoot().getChildren().forEach(child => {
    const getAllNodeKeys = (node: any): void => {
      nodesBefore.add(node.getKey());
      node.getChildren?.()?.forEach(getAllNodeKeys);
    };
    getAllNodeKeys(child);
  });

  // Use standard wrapping - Lexical will handle the splitting
  $wrapSelectionInMarkNode(
    selection,
    false, // Don't merge - maintain distinct comment boundaries
    commentData.id,
    (ids) => $createCommentNode(commentData, ids) as MarkNode
  );

  // Find all newly created comment nodes and ensure they have the same groupId
  $getRoot().getChildren().forEach(child => {
    const updateNewCommentNodes = (node: any): void => {
      if ($isCommentNode(node) && !nodesBefore.has(node.getKey())) {
        const nodeData = node.getCommentData();
        if (nodeData.id === commentData.id && !nodeData.groupId) {
          // This is a newly created split node - assign the groupId
          node.setCommentData({ ...nodeData, groupId: commentData.groupId });
        }
      }
      node.getChildren?.()?.forEach(updateNewCommentNodes);
    };
    updateNewCommentNodes(child);
  });
}

interface CommentPluginProps {
  onAddComment?: (commentData: CommentData) => void;
  onUpdateComment?: (commentId: string, commentData: CommentData) => void;
  onResolveComment?: (commentId: string) => void;
  onDeleteComment?: (commentId: string) => void;
  sync?: () => void;
}

export default function CommentPlugin({
  onAddComment,
  onUpdateComment,
  onResolveComment,
  onDeleteComment,
  sync,
}: CommentPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [popoverCommentData, setPopoverCommentData] = useState<CommentData | null>(null);

  // Register commands
  useEffect(() => {
    const unregisterAddComment = editor.registerCommand(
      ADD_COMMENT_COMMAND,
      (commentData: CommentData) => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            console.log('Creating comment node:', commentData);
            // Use our safe wrapping function
            $wrapInComment(selection, commentData);
            console.log('Comment node created and wrapped');
            onAddComment?.(commentData);
          }
        }, { onUpdate: sync, discrete: true });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterToggleComment = editor.registerCommand(
      TOGGLE_COMMENT_COMMAND,
      (commentId: string) => {
        setActiveCommentId(activeCommentId === commentId ? null : commentId);
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterDeleteComment = editor.registerCommand(
      DELETE_COMMENT_COMMAND,
      (commentId: string) => {
        editor.update(() => {
          // Find one node to get the group ID
          const firstNode = findCommentNodeById(commentId);
          if (!firstNode) return;

          const data = firstNode.getCommentData();
          const groupId = data.groupId || data.id;

          // Find ALL nodes in the group
          const allNodes = findAllCommentNodesInGroup(groupId);

          // Delete all nodes in the group
          allNodes.forEach(commentNode => {
            // Get all child nodes (the text content)
            const children = commentNode.getChildren();

            // Insert children before the comment node
            for (const child of children) {
              commentNode.insertBefore(child);
            }

            // Now remove the empty comment node
            commentNode.remove();
          });

          onDeleteComment?.(commentId);
        }, { onUpdate: sync, discrete: true });
        setActiveCommentId(null);
        setPopoverPosition(null);
        setPopoverCommentData(null);
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterAddComment();
      unregisterToggleComment();
      unregisterDeleteComment();
    };
  }, [editor, activeCommentId, onAddComment, onDeleteComment, sync]);

  // Handle selection changes to detect comment clicks
  useEffect(() => {
    const unregister = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();

            // Check if we're in a comment node
            let commentNode: CommentNode | null = null;
            let currentNode: any = anchorNode;

            while (currentNode && !commentNode) {
              if ($isCommentNode(currentNode)) {
                commentNode = currentNode;
                break;
              }
              const parent = currentNode.getParent();
              currentNode = parent || null;
            }

            if (commentNode) {
              // We're in a comment, but don't auto-show popover
              // User needs to click specifically on the comment mark
            } else {
              // Clear active comment if we're outside
              setActiveCommentId(null);
              setPopoverPosition(null);
              setPopoverCommentData(null);
            }
          }
        });
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return unregister;
  }, [editor]);

  // Handle DOM clicks for comment marks
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const commentMark = target.closest('.comment-mark');

      if (commentMark) {
        event.preventDefault();
        event.stopPropagation();

        const commentId = commentMark.getAttribute('data-comment-id');
        const isResolved = commentMark.getAttribute('data-resolved') === 'true';
        const isOverlap = commentMark.getAttribute('data-overlap') === 'true';

        // Don't show popover for resolved comments
        if (commentId && !isResolved) {
          // Find the comment node in the editor state
          editor.getEditorState().read(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              // Find the comment node with this ID
              const commentNode = findCommentNodeById(commentId);
              if (commentNode) {
                const rect = commentMark.getBoundingClientRect();
                setPopoverPosition({
                  x: rect.right + 10,
                  y: rect.top
                });

                // For overlapping comments, we still show the primary comment data
                // but the user can see the overlap styling
                setPopoverCommentData(commentNode.getCommentData());
                setActiveCommentId(commentId);
              }
            }
          });
        }
      }
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener('click', handleClick);
      return () => {
        editorElement.removeEventListener('click', handleClick);
      };
    }
  }, [editor]);

  const findCommentNodeById = (commentId: string): CommentNode | null => {
    let foundNode: CommentNode | null = null;

    editor.getEditorState().read(() => {
      const visitNode = (node: any) => {
        if ($isCommentNode(node)) {
          const data = node.getCommentData();
          // Check both primary comment ID and any overlapping comment IDs
          if (data.id === commentId || data.groupId === commentId || node.hasCommentId(commentId)) {
            foundNode = node;
            return;
          }
        }

        const children = node.getChildren?.();
        if (children) {
          for (const child of children) {
            visitNode(child);
            if (foundNode) return;
          }
        }
      };

      const root = editor.getEditorState()._nodeMap.get('root');
      if (root) {
        visitNode(root);
      }
    });

    return foundNode;
  };

  // Find ALL nodes in a comment group (for operations that need to update all splits)
  const findAllCommentNodesInGroup = (commentIdOrGroupId: string): CommentNode[] => {
    const foundNodes: CommentNode[] = [];

    editor.getEditorState().read(() => {
      const visitNode = (node: any) => {
        if ($isCommentNode(node)) {
          const data = node.getCommentData();
          // Include nodes that match by ID or groupId
          if (data.id === commentIdOrGroupId || data.groupId === commentIdOrGroupId) {
            foundNodes.push(node);
          }
        }

        const children = node.getChildren?.();
        if (children) {
          for (const child of children) {
            visitNode(child);
          }
        }
      };

      const root = editor.getEditorState()._nodeMap.get('root');
      if (root) {
        visitNode(root);
      }
    });

    return foundNodes;
  };

  const handleUpdateComment = (commentId: string, newContent: string) => {
    editor.update(() => {
      // Find one node to get the current data
      const firstNode = findCommentNodeById(commentId);
      if (!firstNode) return;

      const currentData = firstNode.getCommentData();
      const groupId = currentData.groupId || currentData.id;

      // Find ALL nodes in the group
      const allNodes = findAllCommentNodesInGroup(groupId);

      // Update all nodes in the group
      allNodes.forEach(node => {
        const updatedData = {
          ...node.getCommentData(),
          content: newContent,
          timestamp: Date.now()
        };
        node.setCommentData(updatedData);
      });

      // Update the popover data
      const updatedData = {
        ...currentData,
        content: newContent,
        timestamp: Date.now()
      };
      setPopoverCommentData(updatedData);

      onUpdateComment?.(commentId, updatedData);
    }, { onUpdate: sync, discrete: true });
  };

  const handleResolveComment = (commentId: string) => {
    editor.update(() => {
      // Find one node to get the current data
      const firstNode = findCommentNodeById(commentId);
      if (!firstNode) return;

      const currentData = firstNode.getCommentData();
      const groupId = currentData.groupId || currentData.id;

      // Find ALL nodes in the group
      const allNodes = findAllCommentNodesInGroup(groupId);

      // Update all nodes in the group
      allNodes.forEach(node => {
        const updatedData = {
          ...node.getCommentData(),
          resolved: true,
          timestamp: Date.now()
        };
        node.setCommentData(updatedData);
      });

      // Update the popover data
      const updatedData = {
        ...currentData,
        resolved: true,
        timestamp: Date.now()
      };
      setPopoverCommentData(updatedData);

      onResolveComment?.(commentId);
    }, { onUpdate: sync, discrete: true });

    // Close popover after a short delay to show the resolved state
    setTimeout(() => {
      setActiveCommentId(null);
      setPopoverPosition(null);
      setPopoverCommentData(null);
    }, 1000);
  };

  const handleDeleteComment = (commentId: string) => {
    editor.dispatchCommand(DELETE_COMMENT_COMMAND, commentId);
  };

  const handleClosePopover = () => {
    setActiveCommentId(null);
    setPopoverPosition(null);
    setPopoverCommentData(null);
  };

  return (
    <>
      {popoverPosition && popoverCommentData && (
        <CommentPopover
          position={popoverPosition}
          commentData={popoverCommentData}
          onUpdateComment={(newContent) => handleUpdateComment(popoverCommentData.id, newContent)}
          onResolveComment={() => handleResolveComment(popoverCommentData.id)}
          onDeleteComment={() => handleDeleteComment(popoverCommentData.id)}
          onClose={handleClosePopover}
        />
      )}
    </>
  );
}